import React, { useState, useEffect } from 'react';
import { Box, Grid, GridItem, Tabs, TabList, Tab, TabPanels, TabPanel, Input, Flex, InputGroup, InputLeftElement, Text, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel, FormHelperText } from '@chakra-ui/react';
import JobCard from './jobCard';
import { SearchIcon } from '@chakra-ui/icons'; 
import axios from 'axios';
import FilterSearch from './filterSearch';
import { useJobListRefreshContext } from '../../utils/context/JobListRefresh';
import Chat from '../../components/global/chat';

const JobList = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]);                                     
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const { jobListRefresh } = useJobListRefreshContext();

  const [assignedJobs, setAssignedJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accountDetails, setAccountDetails] = useState({
    accountName: '',
    accountNumber: '',
    bankName: '',
    sortCode: '',
    ifscCode: ''
  });
  const [hasAccountDetails, setHasAccountDetails] = useState(false);
  const [userCountry, setUserCountry] = useState('');

  useEffect(() => {
    const fetchJobSuggestions = async () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
          console.error('No ticket token found in sessionStorage');
          return;
      }
      
      try {
          const response = await axios.get(
              `${process.env.REACT_APP_SERVER_URL}/applicant/get-job-suggestions`,
              {
                  headers: {
                      Authorization: `Bearer ${ticketToken}`
                  }
              }
          );
          console.log('Job suggestions:', response.data);
          setJobs(response.data.data);
      } catch (error) {
          console.error('Error fetching job suggestions:', error);
      }
    };

    const fetchAppliedJobs = async () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/applicant/get-applied-jobs`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );
        console.log('Applied jobs:', response.data);
        setAppliedJobs(response.data.data);
      } catch (error) {
        console.error('Error fetching applied jobs:', error);
      }
    };

    const fetchAssignedJobs = async () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        return;
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/applicant/get-assigned-jobs`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );
        console.log('Assigned jobs:', response.data);
        setAssignedJobs(response.data.data);
      } catch (error) {
        console.error('Error fetching assigned jobs:', error);
      }
    };

    const fetchSavedJobs = async () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/applicant/get-saved-jobs`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );
        console.log('saved jobs:', response.data);
        setSavedJobs(response.data.data);
      } catch (error) {
        console.error('Error fetching saved jobs:', error);
      }
    };

    const fetchCompletedJobs = async () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/applicant/get-completed-jobs`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );
        console.log('Completed jobs:', response.data);
        setCompletedJobs(response.data.data);
      } catch (error) {
        console.error('Error fetching completed jobs:', error);
      }
    };

    const fetchApplicantDetails = async () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/applicant/get-applicant-details`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );

        const data = response.data.data;
        
        setUserCountry(data.address?.country?.name || '');
        
        if (data.accountName && data.accountNumber && data.bankName) {
          const details = {
            accountName: data.accountName,
            accountNumber: data.accountNumber,
            bankName: data.bankName,
            sortCode: data.sortCode || '',
            ifscCode: data.ifscCode || ''
          };
          
          setAccountDetails(details);
          setHasAccountDetails(true);
        }
      } catch (error) {
        console.error('Error fetching applicant details:', error);
      }
    };

    if (selectedCategory === 'All') {
      fetchJobSuggestions();
    } else if (selectedCategory === 'Applied') {
      fetchAppliedJobs();
    } else if (selectedCategory === 'Assigned') {
      fetchAssignedJobs();
    } else if (selectedCategory === 'Saved') {
      fetchSavedJobs();
    } else if (selectedCategory === 'Completed') {
      fetchCompletedJobs();
      fetchApplicantDetails();
    }

  }, [jobListRefresh, selectedCategory]);

  const handleJobClick = (job) => setSelectedJob(job);
  const handleChatClick = (jobId) => {
    console.log("Navigating to chat with job ID:", jobId);
    setSelectedJob(jobId);
  };

  const handleFilterResults = (filteredJobs) => {
    console.log('Filtered jobs received:', filteredJobs); // Debugging log
    setJobs(filteredJobs);
  };
  
  const categories = ['All', 'Applied', 'Assigned', 'Saved', 'Completed'];

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAccountDetails({ ...accountDetails, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        return;
      }

      const payload = {
        accountName: accountDetails.accountName,
        accountNumber: accountDetails.accountNumber,
        bankName: accountDetails.bankName
      };
      
      if (userCountry === 'United Kingdom') {
        payload.sortCode = accountDetails.sortCode;
      } else if (userCountry === 'India') {
        payload.ifscCode = accountDetails.ifscCode;
      }

      console.log('Sending payload:', payload); // Debug log to verify payload

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/applicant/update-bank-details`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${ticketToken}`
          }
        }
      );

      if (response.status === 200) {
        console.log('Bank details saved successfully');
        setHasAccountDetails(true);
        handleCloseModal();
      }
    } catch (error) {
      console.error('Error saving bank details:', error);
    }
  };

  return (
    <Box p={{ base: 2, md: 4 }}>
      <Tabs variant="unstyled" onChange={(index) => setSelectedCategory(categories[index])}>
        <Box overflowX="auto" mb={4}>
          <TabList 
            mr={{ base: 2, md: 4 }} 
            ml={{ base: 2, md: 4 }} 
            borderBottom="1px solid" 
            borderColor="gray.200"
            whiteSpace="nowrap"
            minWidth="min-content"
          >
            {categories.map((category, index) => (
              <Tab
                key={index}
                color="black"
                _selected={{ 
                  fontWeight: "bold", 
                  color: 'black', 
                  borderBottom: '3px solid', 
                  borderColor: 'blue.500', 
                  borderRadius: 'none' 
                }}
                _focus={{ boxShadow: 'none' }}
                _hover={{ background: 'none' }}
                py={2}
                px={{ base: 2, md: 3 }}
                mr={{ base: 2, md: 4 }}
                mb={0.5}
                fontSize={{ base: 'sm', md: 'md' }}
              >
                {category}
              </Tab>
            ))}
          </TabList>
        </Box>

        <Flex 
          justifyContent="flex-end" 
          mb={4} 
          mr={{ base: 2, md: 4 }}
          flexDirection={{ base: 'column', sm: 'row' }}
          gap={{ base: 2, sm: 0 }}
        >
          <InputGroup 
            width={{ base: "100%", sm: "150px" }} 
            mr={{ base: 0, sm: 4 }}
          >
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search jobs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
          
          {selectedCategory === 'All' && (
            <Box width={{ base: "100%", sm: "auto" }}>
              <FilterSearch onFilterApply={handleFilterResults} />
            </Box>
          )}
          
          {selectedCategory === 'Completed' && (
            <Button 
              colorScheme="blue" 
              onClick={handleOpenModal}
              width={{ base: "100%", sm: "auto" }}
            >
              {hasAccountDetails ? 'Edit Bank Details' : 'Add Bank Details'}
            </Button>
          )}
        </Flex>

        <TabPanels>
          {categories.map((category, index) => (
            <TabPanel key={index} px={{ base: 2, md: 4 }}>
              <Grid
                templateColumns={{
                  base: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)'
                }}
                gap={{ base: 3, md: 6 }}
              >
                {(category === 'All' ? jobs : category === 'Applied' ? appliedJobs : category === 'Assigned' ? assignedJobs : category === 'Saved' ? savedJobs : category === 'Completed' ? completedJobs : jobs)
                  .filter(job => {
                    if (category === 'All' || category === 'Applied' || category === 'Assigned' || category === 'Saved' || category === 'Completed') {
                      return job.jobTitle.toLowerCase().includes(searchQuery.toLowerCase());
                    }
                    return job.category === category && job.jobTitle.toLowerCase().includes(searchQuery.toLowerCase());
                  })
                  .map((job, index) => (
                    <GridItem key={index}>
                      <JobCard 
                        {...job} 
                        onClick={() => handleJobClick(job)} 
                        isApplied={category === 'Applied'}
                        category={category}
                        onChatClick={category === 'Assigned' ? () => handleChatClick(job.jobId) : undefined}
                      />
                    </GridItem>
                  ))}
                {((category === 'All' && jobs.length === 0) ||
                  (category === 'Applied' && appliedJobs.length === 0) ||
                  (category === 'Assigned' && assignedJobs.length === 0) ||
                  (category === 'Saved' && savedJobs.length === 0) ||
                  (category === 'Completed' && completedJobs.length === 0)) && (
                  <Text textAlign="center" mt={4} color="gray.500">
                    No jobs found
                  </Text>
                )}
              </Grid>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} size={{ base: "sm", md: "md" }}>
        <ModalOverlay />
        <ModalContent mx={{ base: 4, md: 'auto' }} my="auto">
          <ModalHeader fontSize={{ base: 'lg', md: 'xl' }}>
            {hasAccountDetails ? 'Edit Account Details' : 'Add Account Details'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl id="accountName" mb={4}>
              <FormLabel fontSize={{ base: 'sm', md: 'md' }}>Account Name</FormLabel>
              <Input 
                name="accountName" 
                value={accountDetails.accountName} 
                onChange={handleInputChange}
                fontSize={{ base: 'sm', md: 'md' }}
              />
            </FormControl>
            <FormControl id="accountNumber" mb={4}>
              <FormLabel fontSize={{ base: 'sm', md: 'md' }}>Account Number</FormLabel>
              <Input 
                name="accountNumber" 
                value={accountDetails.accountNumber} 
                onChange={handleInputChange} 
                fontSize={{ base: 'sm', md: 'md' }}
                type="number"
                isInvalid={accountDetails.accountNumber && 
                  (accountDetails.accountNumber.length < 9 || 
                   accountDetails.accountNumber.length > 18)}
              />
              {userCountry === 'India' && (
                <FormHelperText>Must be 9-18 digits</FormHelperText>
              )}
            </FormControl>
            <FormControl id="bankName" mb={4}>
              <FormLabel fontSize={{ base: 'sm', md: 'md' }}>Bank Name</FormLabel>
              <Input 
                name="bankName" 
                value={accountDetails.bankName} 
                onChange={handleInputChange} 
                fontSize={{ base: 'sm', md: 'md' }} 
              />
            </FormControl>
            
            {userCountry === 'United Kingdom' ? (
              <FormControl id="sortCode" mb={4}>
                <FormLabel fontSize={{ base: 'sm', md: 'md' }}>Sort Code</FormLabel>
                <Input 
                  name="sortCode" 
                  value={accountDetails.sortCode} 
                  onChange={handleInputChange} 
                  fontSize={{ base: 'sm', md: 'md' }}
                  placeholder="XX-XX-XX" 
                />
              </FormControl>
            ) : userCountry === 'India' ? (
              <FormControl id="ifscCode" mb={4}>
                <FormLabel fontSize={{ base: 'sm', md: 'md' }}>IFSC Code</FormLabel>
                <Input 
                  name="ifscCode" 
                  value={accountDetails.ifscCode} 
                  onChange={handleInputChange} 
                  fontSize={{ base: 'sm', md: 'md' }}
                  placeholder="AAAA0123456"
                  isInvalid={accountDetails.ifscCode && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(accountDetails.ifscCode)}
                />
                <FormHelperText>Format: AAAA0123456 (11 characters)</FormHelperText>
              </FormControl>
            ) : null}
          </ModalBody>
          <ModalFooter flexDirection={{ base: 'column', sm: 'row' }} gap={{ base: 2, sm: 0 }}>
            <Button 
              colorScheme="blue" 
              mr={{ base: 0, sm: 3 }}
              w={{ base: '100%', sm: 'auto' }}
              onClick={handleSubmit}
            >
              Save
            </Button>
            <Button 
              variant="ghost" 
              onClick={handleCloseModal}
              w={{ base: '100%', sm: 'auto' }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default JobList;